<template>
  <section class="py-16 sm:py-20" :class="sectionClass">
    <div
      class="grid gap-10 items-stretch px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-[420px_minmax(0,1fr)]"
    >
      <div
        class="flex flex-col justify-between p-8 h-full min-h-[420px] text-white rounded-3xl bg-gradient-to-br from-[#003a52] via-[#004d6b] to-kali-blue-600 lg:p-10"
      >
        <div>
          <span
            class="inline-flex items-center px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-kali-blue-100 rounded-full border border-white/20 bg-white/10"
          >
            Perguntas frequentes
          </span>
          <h2 class="mt-4 text-3xl font-extrabold leading-tight">{{ title }}</h2>
          <p v-if="description" class="mt-4 text-base leading-7 text-slate-200">
            {{ description }}
          </p>
        </div>
        <div class="mt-6">
          <button
            v-if="ctaOpensModal && ctaLabel"
            type="button"
            @click="openConsultorModal"
            class="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-xl shadow-lg transition bg-kali-gold text-[#004d6b] hover:bg-amber-400"
          >
            {{ ctaLabel }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <a
            v-else-if="ctaHref && ctaLabel"
            :href="ctaHref"
            :target="ctaHref.startsWith('http') ? '_blank' : undefined"
            :rel="ctaHref.startsWith('http') ? 'noopener' : undefined"
            class="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-xl shadow-lg transition bg-kali-gold text-[#004d6b] hover:bg-amber-400"
          >
            {{ ctaLabel }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
      <div class="space-y-3">
        <div
          v-for="(item, idx) in items"
          :key="idx"
          class="bg-white rounded-2xl border transition"
          :class="isOpen(idx) ? 'border-kali-blue-200 shadow-md' : 'border-gray-200 hover:border-kali-blue-100'"
        >
          <h3 class="text-sm">
            <button
              :id="`faq-header-${idx}`"
              class="flex items-center justify-between gap-4 px-5 py-4 w-full text-left"
              type="button"
              :aria-expanded="isOpen(idx) ? 'true' : 'false'"
              :aria-controls="`faq-panel-${idx}`"
              @click="toggle(idx)"
            >
              <span class="text-base font-semibold text-gray-900 sm:text-lg">
                {{ item.question }}
              </span>
              <span
                class="flex items-center justify-center flex-none w-8 h-8 rounded-full transition-all duration-200"
                :class="isOpen(idx) ? 'bg-kali-blue-600 text-white rotate-180' : 'bg-kali-blue-50 text-kali-blue-600'"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>
          </h3>
          <section
            :id="`faq-panel-${idx}`"
            role="region"
            :aria-labelledby="`faq-header-${idx}`"
            class="overflow-hidden transition-all duration-300 ease-in-out"
            :class="isOpen(idx) ? 'max-h-96' : 'max-h-0'"
          >
            <p class="px-5 pb-5 text-sm leading-relaxed text-gray-600 sm:text-base">
              {{ item.answer }}
            </p>
          </section>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
defineOptions({ name: "FaqPanel" });
import { ref } from "vue";

const { items, description, ctaLabel, ctaHref, ctaOpensModal } = defineProps({
  title: {
    type: String,
    default: "Perguntas frequentes",
  },
  items: {
    type: Array,
    default: () => [],
  },
  description: {
    type: String,
    default:
      "Reunimos respostas para as dúvidas mais comuns. Se precisar de ajuda, nossa equipe está pronta para apoiar!",
  },
  ctaLabel: {
    type: String,
    default: "Falar com Consultor",
  },
  ctaHref: {
    type: String,
    default: "/suporte",
  },
  ctaOpensModal: {
    type: Boolean,
    default: false,
  },
  sectionClass: {
    type: String,
    default: "bg-white",
  },
});

function openConsultorModal() {
  window.dispatchEvent(new CustomEvent("open-consultor-modal"));
}

const openIndex = ref(0);

function toggle(idx) {
  openIndex.value = openIndex.value === idx ? null : idx;
}

function isOpen(idx) {
  return openIndex.value === idx;
}
</script>
